import * as Icons from "@nova/component-icons"
import { AppDispatch, UpdateAction } from "@nova/constants"
import classNames from "classnames"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectEntityById } from "panels/content/side-panel/slice"
import {
    clearSelection,
    selectRenamingInspectorPanelId,
    setRenamingInspectorPanelId,
} from "panels/content/side-panel/slice.new"
import { initialTitleIsPlaceholder } from "../../icons/module"
import { useEffect, useRef } from "react"
import { LayerPluginSpec, PluginSpec } from "plugins/plugin-registry"
import { setActiveTool } from "plugins/Toolbox/tool-registry"
import { Tooltip } from "panels/content/top-bar/TODO-put-in-package/Tooltip"
import { InspectorPanelPlugin } from "../../useInspectorPanel"
import { closeHotspotMultiRemoval } from "features/data-management/hotspots/slice"
import { RootState } from "integrations/redux"
import { useIsEditor } from "tools/hooks/useIsEditor"

export type InspectorPanelAction = {
    icon: Icons.IconComponent
    onClick: (id: string) => void
    tooltipText: string | undefined
}

export function getGenericUseHeaderActions(spec: LayerPluginSpec) {
    const useHeaderActions = (id: string) => {
        const dispatch = useAppDispatch()
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const isEditor = useIsEditor()
        const actions: InspectorPanelAction[] = []
        actions.push({
            icon: Icons.ZoomTo,
            onClick: () => spec.zoomTo(entity),
            tooltipText: "Zoom to Location",
        })
        if (entity.locked) {
            actions.push({
                icon: Icons.LayerLocked,
                onClick: () => {
                    if (!isEditor) return
                    dispatch(spec.updateAction({ id, updates: { locked: false } }))
                },
                tooltipText: "Unlock",
            })
        } else {
            actions.push({
                icon: Icons.LayerUnlocked,
                onClick: () => {
                    if (!isEditor) return
                    dispatch(spec.updateAction({ id, updates: { locked: true } }))
                },
                tooltipText: "Lock",
            })
        }
        if (entity.visible) {
            actions.push({
                icon: Icons.VisibilityEye,
                onClick: () => {
                    if (!isEditor) return
                    dispatch(spec.updateAction({ id, updates: { visible: false } }))
                },
                tooltipText: "Hide",
            })
        } else {
            actions.push({
                icon: Icons.VisibilityEyeOff,
                onClick: () => {
                    if (!isEditor) return
                    dispatch(spec.updateAction({ id, updates: { visible: true } }))
                },
                tooltipText: "Show",
            })
        }
        return actions
    }
    return useHeaderActions
}

// TODO CONNOR: Change this to accept LayerPluginSpec only (right now its typed as any)
export function getGenericInspectorPanelHeader(spec: PluginSpec) {
    const GenericInspectorPanelHeader = ({
        id,
        plugin,
    }: {
        id: string
        plugin: InspectorPanelPlugin
    }) => {
        return (
            <div className="flex flex-col px-4 pt-4 pb-3">
                <GenericInspectorPanelHeaderTopRow typeTitle={spec.displayName} />
                <GenericInspectorPanelRenamableLayerName
                    id={id}
                    updateFn={spec.updateAction}
                    className="mt-[14px] mb-2"
                />
                <GenericInspectorPanelSubtitles id={id} plugin={plugin} />
                <GenericInspectorPanelHeaderActions id={id} plugin={plugin} />
            </div>
        )
    }
    return GenericInspectorPanelHeader
}

export function getGenericMockedInspectorPanelHeader(spec: PluginSpec) {
    const GenericMockedInspectorPanelHeader = () => {
        return (
            <div className="flex flex-col px-4 pt-4 pb-3">
                <GenericInspectorPanelHeaderTopRow
                    typeTitle={spec.mockedDisplayName || spec.displayName}
                />
            </div>
        )
    }
    return GenericMockedInspectorPanelHeader
}

export function GenericInspectorPanelHeaderTopRow({
    typeTitle,
    Icon,
}: {
    typeTitle: string
    Icon?: Icons.IconComponent
}) {
    const dispatch = useAppDispatch()
    return (
        <div className="flex justify-between items-center">
            <div className="flex gap-2.5 items-center">
                {Icon && <Icons.Icon iconClassName="text-gray-300" Icon={Icon} />}
                <small className="xs text-gray-300">{typeTitle}</small>
            </div>
            <Icons.SmallIcon
                Icon={Icons.XClose}
                className="text-gray-300 hover:text-gray-0 cursor-pointer"
                onClick={() => {
                    dispatch(clearSelection())
                    dispatch(setActiveTool(undefined))
                    dispatch(closeHotspotMultiRemoval())
                }}
            />
        </div>
    )
}

export function GenericInspectorPanelRenamableLayerName({
    id,
    updateFn,
    className = "",
}: {
    id: string
    updateFn:
        | UpdateAction
        | ((params: {
              id: string
              updates: Record<string, unknown>
          }) => (dispatch: AppDispatch, getState: () => RootState) => void)
    className?: string
}) {
    const dispatch = useAppDispatch()
    const inputRef = useRef<HTMLDivElement>(null)
    const projectId = useAppSelector(selectActivatedFireId)
    const { isEditor } = useWorkspacePermissions(projectId || "")
    const entity = useAppSelector(selectEntityById(id))
    const renamingInspectorPanelId = useAppSelector(selectRenamingInspectorPanelId)
    useEffect(() => {
        if (renamingInspectorPanelId === id) {
            inputRef.current?.focus()
        }
    }, [id, renamingInspectorPanelId])
    return (
        <div className={classNames("relative", className)}>
            <RenamableText
                ref={inputRef}
                Tag="h2"
                title={entity.name}
                id="inspector-layer-title"
                className={classNames("text-gray-0", {
                    "cursor-text": isEditor,
                })}
                isRenaming={renamingInspectorPanelId === entity.id}
                isFullWidth={true}
                shouldHighlightOnHover={isEditor}
                shouldMakeTextSelectable={!isEditor}
                initialTitleIsPlaceholder={initialTitleIsPlaceholder(entity.name)}
                onClick={() => {
                    if (isEditor) dispatch(setRenamingInspectorPanelId({ id: entity.id }))
                }}
                onCancelRenaming={() => dispatch(setRenamingInspectorPanelId({ id: undefined }))}
                onConfirmRenaming={(_, name) => {
                    if (!isEditor) return
                    dispatch(
                        updateFn({
                            id: entity.id,
                            updates: { name },
                        })
                    )
                    dispatch(setRenamingInspectorPanelId({ id: undefined }))
                }}
                maxLines={3}
            />
        </div>
    )
}

export function GenericInspectorPanelSubtitles({
    id,
    plugin,
}: {
    id: string
    plugin: InspectorPanelPlugin
}) {
    const subtitles = plugin.useSubtitles(id)
    if (Array.isArray(subtitles)) {
        if (!subtitles.length) return null
        return (
            <div className="w-full flex gap-x-3 gap-y-1 mb-3">
                {subtitles.map((s, i) => (
                    <small
                        key={i}
                        className="text-ellipsis overflow-hidden whitespace-nowrap text-gray-300 mono"
                    >
                        {s}
                    </small>
                ))}
            </div>
        )
    }
    return <div className="mb-3">{subtitles}</div>
}

export function GenericInspectorPanelHeaderActions({
    id,
    plugin,
}: {
    id: string
    plugin: InspectorPanelPlugin
}) {
    const actions = plugin.useHeaderActions(id)
    return (
        <div className="flex gap-[6px] mt-1">
            {actions.map((action, i) => (
                <div key={i} className="relative group/tooltip">
                    <Icons.ExtraSmallIcon
                        className="text-gray-400 hover:text-gray-100 cursor-pointer"
                        Icon={action.icon}
                        onClick={() => action.onClick(id)}
                    />
                    {action.tooltipText && <Tooltip title={action.tooltipText} />}
                </div>
            ))}
        </div>
    )
}
